/**
 * Builds GettingThereCard options from real `Location` fields only (WEB-415).
 *
 * Extracted from `EventDetailTemplate` so SSR Places enrichment (FRE-1292) and
 * the client template share one honesty gate. Never invents `$15`, train ETAs,
 * or bike rows.
 */
import type { GettingThereOption } from '@/components/organisms/GettingThereCard';
import { walkDetailFromMeters, walkMetricMinutes } from '@/lib/gettingThere/walkDistanceCopy';

/** Coarse walk pace used only when converting real metres → approximate minutes. */
export const WALK_METERS_PER_MINUTE = 80;

/**
 * Location fields the getting-there builder reads. Matches the OpenAPI
 * `Location` schema (API-1720) — all optional/nullable, same as the wire.
 */
export type GettingThereLocationInput = {
  walkingDistanceToTransit?: number | null;
  transitStops?: string[] | null;
  hasParking?: boolean | null;
};

/**
 * Builds the "Getting there" routing options from ONLY real `Location` data
 * (WEB-415). The card must never fabricate a travel time, price, or option:
 *
 * - Walk: derived from the real `walkingDistanceToTransit` (metres). We
 *   surface the real distance and a coarse walk-time estimate (80 m/min), and
 *   the detail restates only the real distance — no invented "WELL-LIT" claim.
 * - Transit: only when `transitStops` is non-empty. We name the real stop but
 *   omit the metric entirely — no transit travel time is derivable from the
 *   data we have today.
 * - Parking: only when `hasParking === true`. We state that parking exists at
 *   the venue but omit any price — no real price is available.
 * - Bike: removed. There is no real bike data, so no bike row is emitted.
 *
 * When nothing resolves, this returns `[]` and the card renders an honest
 * "Get directions" link instead of a fabricated list.
 */
export function buildGettingThereOptionsFromLocation(
  location: GettingThereLocationInput | null | undefined,
): GettingThereOption[] {
  if (!location) return [];
  const options: GettingThereOption[] = [];
  if (
    location.walkingDistanceToTransit !== null &&
    location.walkingDistanceToTransit !== undefined
  ) {
    const meters = Math.max(0, Math.round(location.walkingDistanceToTransit));
    const walkMinutes = Math.max(1, Math.round(meters / WALK_METERS_PER_MINUTE));
    options.push({
      layer: 'walk',
      name: 'Walk to nearest transit',
      detail: walkDetailFromMeters(meters),
      // `walkMinutes` is a coarse estimate derived from the real distance at a
      // fixed 80 m/min pace, not a measured time. The `~` prefix keeps the row
      // honest (WEB-415) — the distance is real, the minutes are approximate.
      metric: walkMetricMinutes(walkMinutes),
    });
  }
  const transitStops = location.transitStops ?? [];
  if (transitStops.length > 0) {
    options.push({
      layer: 'transit',
      name: transitStops[0]!,
      detail: 'Nearest stop',
    });
  }
  if (location.hasParking === true) {
    options.push({
      layer: 'parking',
      name: 'On-site parking',
      detail: 'At venue',
    });
  }
  return options;
}
